import { Link } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { JournalLine, JournalTaxTypeEnum } from '../../types/journal';

import { DataGrid } from '@cimplico/components';
import { NatureEnum } from '../../types/nature';
import Currency from '../../components/currency/Currency';
import { WorksheetStatusIcon } from '../worksheet-v2/WorksheetStatusIcon';

type JournalLineRowProps = {
    id: string;
    taxType?: JournalTaxTypeEnum;
    journalLine: JournalLine;
};

function JournalLineRow({ id, taxType, journalLine }: JournalLineRowProps): JSX.Element {
    const { shortId } = useFirmContext();
    const { id: workpaperId } = useWorkpaperContext();

    const showTaxRate = taxType !== JournalTaxTypeEnum.NO_TAX;

    return (
        <DataGrid.Row id={id}>
            <div className={`flex flex-col ${showTaxRate ? 'lg:flex-row lg:gap-4' : 'md:gap-4 md:flex-row'} w-full`}>
                <div className={`grid gap-2 ${showTaxRate ? 'lg:gap-4' : 'md:gap-4'} w-full items-center grid-cols-[1.3fr_1.5fr]`}>
                    <div className="text-gray-800 text-xs py-1">{journalLine.description || '-'}</div>
                    <div className="flex items-center text-xs relative">
                        {journalLine.account ? (
                            journalLine.worksheet ? (
                                <>
                                    <div className="absolute -left-[1.375rem]">
                                        <WorksheetStatusIcon status={journalLine.worksheet.status} smallIcon />
                                    </div>
                                    <Link
                                        to={`/${shortId}/workpapers/${workpaperId}/trial-balance/${journalLine.worksheet.id}?tab=journals`}
                                    >
                                        <div className="text-indigo-600 font-medium">
                                            {journalLine.account.code} - {journalLine.account.description}
                                        </div>
                                    </Link>
                                </>
                            ) : (
                                <div className="text-gray-600">
                                    {journalLine.account.code} - {journalLine.account.description}
                                </div>
                            )
                        ) : (
                            <div className="text-gray-800">-</div>
                        )}
                    </div>
                </div>
                <div className={`grid gap-2 w-full items-center ${showTaxRate ? 'grid-cols-3 lg:gap-4' : 'grid-cols-2 md:gap-4'}`}>
                    {showTaxRate && (
                        <div className="text-gray-500 text-xs">
                            {journalLine.taxRate ? `${journalLine.taxRate.name} (${journalLine.taxRate.rate}%)` : '-'}
                        </div>
                    )}
                    <div className="flex gap-2 text-xs pr-4 justify-end">
                        {journalLine.lineType === NatureEnum.DEBIT ? (
                            <Currency value={journalLine.lineAmount} className="text-gray-800 font-medium" />
                        ) : (
                            <div className="text-gray-800">-</div>
                        )}
                        <div className="text-gray-500">Dr</div>
                    </div>
                    <div className="flex gap-2 text-xs pr-4 justify-end">
                        {journalLine.lineType === NatureEnum.CREDIT ? (
                            <Currency value={journalLine.lineAmount} className="text-gray-800 font-medium" />
                        ) : (
                            <div className="text-gray-800">-</div>
                        )}
                        <div className="text-gray-500">Cr</div>
                    </div>
                </div>
            </div>
        </DataGrid.Row>
    );
}

export default JournalLineRow;
